import { Icon } from '@amzn/awsui-components-react';
import Link from '@amzn/awsui-components-react/polaris/link';
import { PropertyFilterProps } from '@amzn/awsui-components-react/polaris/property-filter';
import StatusIndicator from '@amzn/awsui-components-react/polaris/status-indicator';
import { AttendanceStatus, Participation, Person, Shift } from '@amzn/red-velvet-api';
import { useTranslation } from 'react-i18next';
import { useMemo } from 'react';
import { ParamsParticipantData } from '../../hooks/attendance/participantSessionStorage';
import { ColumnMap } from '../../hooks/tableColumns';
import { formatAttendanceTimeDisplay } from './time';

// This is pulled from the PAPI params size in https://code.amazon.com/packages/RedVelvetApiTypescriptLambda/blobs/mainline/--/src/clients/papiClient.ts
export const PEOPLE_PAGE_SIZE = 25;

export interface Amazonian {
  name: string;
  alias: string;
  managerLogin?: string;
  building?: string;
  signedUp?: boolean;
  trackedBy?: string;
  attendance?: string;
  trackedTime?: number;
  shiftId?: string;
  originalShiftId?: string;
  shiftName?: string;
  shiftDate?: string;
  shiftTime?: string;
}

export type ParticipationStatus = AttendanceStatus | 'PENDING';

export type ParticipationWithFullname = Participation & {
  attendanceStatus?: ParticipationStatus;
  volunteerName: string | ((item: Participation) => string);
};

// GOLDEN PATH NOTE
// Tables require several properties to define columns that can cause a large amount of repetitive copy paste
// Whenever possible, table columns should be defined as a map across the different data keys, to prevent user errors in the copy paste
// The ColumnMap type and associated useTableColumns hook perform the most common mapping for tables
export const columnMap: ColumnMap<Amazonian> = {
  name: (item: Amazonian) => item.name,
  alias: (item: Amazonian) => (
    <Link target="_blank" href={`https://phonetool.amazon.com/users/${item.alias}`}>
      {item.alias}
    </Link>
  ),
  managerLogin: (item: Amazonian) => (
    <Link target="_blank" href={`https://phonetool.amazon.com/users/${item.managerLogin}`}>
      {item.managerLogin}
    </Link>
  ),
  building: (item: Amazonian) => item.building || '',
};

/**
 * Displays attendance status with appropriate color coding and localized text.
 * @param props - Component props
 * @param props.status - The attendance status to display
 * @returns StatusIndicator component with appropriate styling
 */
export function AttendanceIndicator({ status }: { status: AttendanceStatus | 'PENDING' }) {
  const { t } = useTranslation(['attendance', 'translation']);

  switch (status) {
    case AttendanceStatus.ATTENDED:
      return (
        <StatusIndicator type={'success'}>
          {t('coordinatorAttendeeTable.coordinatorTracking.setAttended')}
        </StatusIndicator>
      );
    case AttendanceStatus.DID_NOT_ATTEND:
      return (
        <StatusIndicator type={'error'}>
          {t('coordinatorAttendeeTable.coordinatorTracking.setDidNotAttend')}
        </StatusIndicator>
      );
    case 'PENDING':
      return (
        <StatusIndicator type={'pending'}>
          {t('coordinatorAttendeeTable.coordinatorTracking.pending')}
        </StatusIndicator>
      );
    default:
      return (
        <StatusIndicator type={'pending'}>
          {t('coordinatorAttendeeTable.coordinatorTracking.pending')}
        </StatusIndicator>
      );
  }
}

export const shiftAttendanceColumnMap: ColumnMap<Amazonian> = {
  name: (item: Amazonian) => item.name,
  alias: (item: Amazonian) => (
    <Link target="_blank" href={`https://phonetool.amazon.com/users/${item.alias}`}>
      {item.alias}
    </Link>
  ),
  managerLogin: (item: Amazonian) => (
    <Link target="_blank" href={`https://phonetool.amazon.com/users/${item.managerLogin}`}>
      {item.managerLogin}
    </Link>
  ),
  building: (item: Amazonian) => item.building || '',
  signedUp: (item: Amazonian) => (item.signedUp ? <Icon name="check" /> : ''),
  trackedBy: (item: Amazonian) =>
    item.trackedBy ? (
      <Link target="_blank" href={`https://phonetool.amazon.com/users/${item.trackedBy}`}>
        {item.trackedBy}
      </Link>
    ) : (
      ''
    ),
  attendance: (item: Amazonian) => (
    <AttendanceIndicator status={item.attendance as AttendanceStatus | 'PENDING'} />
  ),
};

function ShiftTimeCell({ value }: { value?: string }) {
  const { t } = useTranslation(['attendance']);
  return <>{value || t('attendeeTable.ongoingShift')}</>;
}

export const eventLevelAttendanceColumnMap: ColumnMap<Amazonian> = {
  name: (item: Amazonian) => item.name,
  alias: (item: Amazonian) => (
    <Link target="_blank" href={`https://phonetool.amazon.com/users/${item.alias}`}>
      {item.alias}
    </Link>
  ),
  shiftName: (item: Amazonian) => item.shiftName || '',
  shiftDate: (item: Amazonian) => <ShiftTimeCell value={item.shiftDate} />,
  shiftTime: (item: Amazonian) => <ShiftTimeCell value={item.shiftTime} />,
  signedUp: (item: Amazonian) => (item.signedUp ? <Icon name="check" /> : ''),
  trackedBy: (item: Amazonian) =>
    item.trackedBy ? (
      <Link target="_blank" href={`https://phonetool.amazon.com/users/${item.trackedBy}`}>
        {item.trackedBy}
      </Link>
    ) : (
      ''
    ),
  attendance: (item: Amazonian) => (
    <AttendanceIndicator status={item.attendance as AttendanceStatus | 'PENDING'} />
  ),
};

export function useAttendanceColumnMap(defaultShiftLength: number, isImmutable = false) {
  const { t } = useTranslation(['attendance', 'translation']);

  return {
    name: (item: Amazonian) => item.name,
    alias: (item: Amazonian) => (
      <Link target="_blank" href={`https://phonetool.amazon.com/users/${item.alias}`}>
        {item.alias}
      </Link>
    ),
    managerLogin: (item: Amazonian) => (
      <Link target="_blank" href={`https://phonetool.amazon.com/users/${item.managerLogin}`}>
        {item.managerLogin}
      </Link>
    ),
    building: (item: Amazonian) => item.building || '',
    signedUp: (item: Amazonian) =>
      item.signedUp ? (
        <Icon name="check" ariaLabel={t('coordinatorAttendeeTable.didSignUp', item)} />
      ) : (
        ''
      ),
    trackedBy: (item: Amazonian) =>
      item.trackedBy ? (
        <Link target="_blank" href={`https://phonetool.amazon.com/users/${item.trackedBy}`}>
          {item.trackedBy}
        </Link>
      ) : (
        ''
      ),
    attendance: (item: Amazonian) => (
      <AttendanceIndicator status={item.attendance as AttendanceStatus | 'PENDING'} />
    ),
    trackedTime: (item: Amazonian) =>
      item.trackedTime
        ? formatAttendanceTimeDisplay(item.trackedTime, t)
        : isImmutable && item.attendance === 'ATTENDED'
          ? formatAttendanceTimeDisplay(defaultShiftLength, t)
          : '',
  };
}

export function useEventLevelAttendanceColumnMap(isImmutable = false, shifts?: Shift[]) {
  const { t } = useTranslation(['attendance', 'translation']);

  const shiftLengthMap = useMemo(() => {
    const map = new Map<string, number>();
    (shifts || []).forEach((shift) => {
      if (shift.shiftId) {
        const startDate = shift.startDateTime ? new Date(shift.startDateTime) : null;
        const endDate = shift.endDateTime ? new Date(shift.endDateTime) : null;
        if (startDate && endDate) {
          map.set(shift.shiftId, (endDate.getTime() - startDate.getTime()) / (1000 * 60));
        }
      }
    });
    return map;
  }, [shifts]);

  return {
    name: (item: Amazonian) => item.name,
    alias: (item: Amazonian) => (
      <Link target="_blank" href={`https://phonetool.amazon.com/users/${item.alias}`}>
        {item.alias}
      </Link>
    ),
    shiftName: (item: Amazonian) => item.shiftName || '',
    shiftDate: (item: Amazonian) => <ShiftTimeCell value={item.shiftDate} />,
    shiftTime: (item: Amazonian) => <ShiftTimeCell value={item.shiftTime} />,
    signedUp: (item: Amazonian) =>
      item.signedUp ? (
        <Icon name="check" ariaLabel={t('coordinatorAttendeeTable.didSignUp', item)} />
      ) : (
        ''
      ),
    trackedBy: (item: Amazonian) =>
      item.trackedBy ? (
        <Link target="_blank" href={`https://phonetool.amazon.com/users/${item.trackedBy}`}>
          {item.trackedBy}
        </Link>
      ) : (
        ''
      ),
    attendance: (item: Amazonian) => (
      <AttendanceIndicator status={item.attendance as AttendanceStatus | 'PENDING'} />
    ),
    trackedTime: (item: Amazonian) => {
      // Self-tracked: always show their tracked time (even if 0)
      if (item.alias === item.trackedBy) {
        return formatAttendanceTimeDisplay(item.trackedTime || 0, t);
      }
      // Coordinator-tracked: show tracked time if set
      if (item.trackedTime) {
        return formatAttendanceTimeDisplay(item.trackedTime, t);
      }
      // Default to shift length for immutable view (final review) if ATTENDED
      if (isImmutable && item.attendance === AttendanceStatus.ATTENDED && item.shiftId) {
        const shiftLength = shiftLengthMap.get(item.shiftId);
        return shiftLength ? formatAttendanceTimeDisplay(shiftLength, t) : '';
      }
      return '';
    },
  };
}

function isAmazonian(o: Amazonian | Participation): o is Amazonian {
  return (o as Amazonian).alias !== undefined;
}

export function getAliases(
  attendees: Amazonian[] | Participation[] | readonly Amazonian[]
): string[] {
  return attendees
    .map((attendee) => (isAmazonian(attendee) ? attendee.alias : attendee.volunteerAlias))
    .filter((alias): alias is string => alias !== undefined);
}

export function getAmazoniansFromPeople(
  people: Person[],
  username?: string,
  shiftLength?: number,
  participantData?: ParamsParticipantData
): Amazonian[] {
  return (people || [])
    .filter((person): person is Person & { alias: string } => !!person.alias)
    .map((person) => {
      const participant = participantData ? participantData[person.alias] || {} : {};
      return {
        ...person,
        name: `${person.lastName}, ${person.firstName}`,
        trackedBy: username,
        signedUp: false,
        attendance: 'ATTENDED',
        ...participant,
      };
    });
}

export function getAmazoniansFromParticipants(
  participants: Participation[],
  participantData?: ParamsParticipantData
): Amazonian[] {
  return (participants || [])
    .filter((p): p is Participation & { volunteerAlias: string } => !!p.volunteerAlias)
    .map((participant) => {
      const participantUrlData = participantData
        ? participantData[participant.volunteerAlias] || {}
        : {};

      return {
        alias: participant.volunteerAlias,
        firstName: participant.volunteerFirstName,
        lastName: participant.volunteerLastName,
        name: `${participant.volunteerLastName}, ${participant.volunteerFirstName}`,
        building: participant.volunteerBuildingName,
        managerLogin: participant.volunteerManagerAlias,
        trackedBy: participant.attendanceUpdatedByAlias,
        signedUp: participant.isSignedUp,
        attendance: participant.attendanceStatus,
        trackedTime: participant.attendanceMinutes,
        shiftId: participant.shiftId,
        ...participantUrlData,
      };
    });
}

export function getParticipantsFromPeople(people?: Person[]): ParticipationWithFullname[] {
  return (people || []).map((person) => ({
    volunteerAlias: person.alias ?? '',
    volunteerName: `${person.lastName}, ${person.firstName}`,
    isSignedUp: false,
    // only non-signup attendees added from Search Amazonian will default to ATTENDED status
    // this allows the removal of accidentally added person by setting them to DID_NOT_ATTEND
    attendanceStatus: 'ATTENDED',
    attendanceMinutes: 0,
    volunteerBuildingName: person.building,
    volunteerManagerAlias: person.managerLogin,
  }));
}

export function convertTokensToPeopleQuery(filters: PropertyFilterProps.Query) {
  const people: string[] = [];
  const managers: string[] = [];
  const buildings: string[] = [];
  const countries: string[] = [];
  const aliases: string[] = [];
  const levels: string[] = [];

  filters.tokens.forEach((token) => {
    switch (token.propertyKey) {
      case 'managerLogin':
        managers.push(token.value);
        break;
      case 'building':
        buildings.push(token.value);
        break;
      case 'country':
        countries.push(token.value);
        break;
      case 'alias':
        aliases.push(token.value);
        break;
      case 'level':
        levels.push(token.value);
        break;
      default:
        people.push(token.value);
        break;
    }
  });
  return {
    people: people.length > 0 ? people.join(',') : undefined,
    managers: managers.length > 0 ? managers.join(',') : undefined,
    buildings: buildings.length > 0 ? buildings.join(',') : undefined,
    countries: countries.length > 0 ? countries.join(',') : undefined,
    aliases: aliases.length > 0 ? aliases.join(',') : undefined,
    levels: levels.length > 0 ? levels.join(',') : undefined,
  };
}
